'use client';

const DB_NAME = 'mpsec_offline_db';
const DB_VERSION = 3;

const STORES = {
  VOTERS: 'voters',
  VERIFICATIONS: 'verifications',
  BOOTH_INFO: 'boothInfo',
  SYNC_QUEUE: 'syncQueue',
  CREDENTIALS: 'credentials',
} as const;

export interface OfflineVoter {
  id: string;
  epicNo: string;
  serialNo: number;
  name: string;
  nameHindi: string;
  fatherHusbandName: string;
  relationType: string;
  age: number;
  gender: string;
  address: string;
  houseNo: string;
  photoUrl: string;
  pollingBoothId: string;
}

export interface OfflineVerification {
  id: string;
  epicNo: string;
  serialNo: number;
  voterName: string;
  fatherHusbandName: string;
  relationType: string;
  gender: string;
  age: number;
  signatureImage: string;
  thumbImage: string;
  verificationType: string;
  pollingBoothId: string;
  officerId: string;
  officerName: string;
  verifiedAt: string;
  synced: number; // 0 = unsynced, 1 = synced (using number because IndexedDB doesn't support boolean keys)
  syncedAt: string | null;
}

export interface OfflineBoothInfo {
  pollingBoothId: string;
  name: string;
  address: string;
  district: string;
  constituency: string;
  constituencyNo: string;
  wardNo: string;
  totalVoters: number;
  downloadedAt: string;
}

export interface SyncQueueItem {
  id: string;
  type: 'verification';
  data: OfflineVerification;
  createdAt: string;
  retryCount: number;
}

export interface OfflineCredential {
  username: string;
  passwordHash: string;
  role: 'admin' | 'officer';
  user: {
    id: string;
    name: string;
    username: string;
    boothId?: string;
  };
  token: string;
  savedAt: string;
}

class OfflineDB {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<IDBDatabase> | null = null;

  private async getDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        this.initPromise = null;
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        // Voters store - indexed by pollingBoothId and epicNo
        if (!db.objectStoreNames.contains(STORES.VOTERS)) {
          const voterStore = db.createObjectStore(STORES.VOTERS, { keyPath: 'id' });
          voterStore.createIndex('pollingBoothId', 'pollingBoothId', { unique: false });
          voterStore.createIndex('epicNo', 'epicNo', { unique: false });
          voterStore.createIndex('serialNo', 'serialNo', { unique: false });
          voterStore.createIndex('booth_epic', ['pollingBoothId', 'epicNo'], { unique: true });
          voterStore.createIndex('booth_serial', ['pollingBoothId', 'serialNo'], { unique: false });
        }

        // Verifications store
        if (!db.objectStoreNames.contains(STORES.VERIFICATIONS)) {
          const verifyStore = db.createObjectStore(STORES.VERIFICATIONS, { keyPath: 'id' });
          verifyStore.createIndex('pollingBoothId', 'pollingBoothId', { unique: false });
          verifyStore.createIndex('synced', 'synced', { unique: false });
          verifyStore.createIndex('booth_synced', ['pollingBoothId', 'synced'], { unique: false });
          verifyStore.createIndex('epicNo', 'epicNo', { unique: false });
        }

        // Booth info store
        if (!db.objectStoreNames.contains(STORES.BOOTH_INFO)) {
          const boothStore = db.createObjectStore(STORES.BOOTH_INFO, { keyPath: 'pollingBoothId' });
          boothStore.createIndex('district', 'district', { unique: false });
        }

        // Sync queue store
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Credentials store for offline login
        if (!db.objectStoreNames.contains(STORES.CREDENTIALS)) {
          const credStore = db.createObjectStore(STORES.CREDENTIALS, { keyPath: 'username' });
          credStore.createIndex('role', 'role', { unique: false });
        }

        // MIGRATION v2→v3: Convert boolean `synced` to number (0/1) for IndexedDB key compatibility
        // IndexedDB does not support boolean keys, so queries like IDBKeyRange.only([boothId, 0])
        // would never match records stored with synced: false
        if (oldVersion < 3 && db.objectStoreNames.contains(STORES.VERIFICATIONS)) {
          const tx = (event.target as IDBOpenDBRequest).transaction;
          if (tx) {
            const store = tx.objectStore(STORES.VERIFICATIONS);
            const getAllRequest = store.getAll();
            getAllRequest.onsuccess = () => {
              const records = getAllRequest.result;
              for (const record of records) {
                if (typeof record.synced === 'boolean') {
                  record.synced = record.synced ? 1 : 0;
                  store.put(record);
                }
              }
            };
          }
        }
      };
    });

    return this.initPromise;
  }

  async init(): Promise<void> {
    await this.getDB();
  }

  // ==================== VOTERS ====================

  async saveVoters(voters: OfflineVoter[]): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VOTERS, 'readwrite');
      const store = tx.objectStore(STORES.VOTERS);

      for (const voter of voters) {
        store.put(voter);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getVoters(boothId: string): Promise<OfflineVoter[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VOTERS, 'readonly');
      const store = tx.objectStore(STORES.VOTERS);
      const index = store.index('pollingBoothId');
      const request = index.getAll(boothId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async searchVoter(query: string, boothId: string): Promise<OfflineVoter[]> {
    const db = await this.getDB();
    const allVoters = await this.getVoters(boothId);
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return [];

    return allVoters.filter((voter) => {
      const epicMatch = voter.epicNo.toLowerCase().includes(normalizedQuery);
      const serialMatch = voter.serialNo.toString() === normalizedQuery;
      const nameMatch = voter.name.toLowerCase().includes(normalizedQuery);
      const nameHindiMatch = voter.nameHindi.toLowerCase().includes(normalizedQuery);
      return epicMatch || serialMatch || nameMatch || nameHindiMatch;
    });
  }

  async getVoterByEpicNo(epicNo: string, boothId: string): Promise<OfflineVoter | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VOTERS, 'readonly');
      const store = tx.objectStore(STORES.VOTERS);
      const index = store.index('booth_epic');
      const request = index.get([boothId, epicNo]);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getVoterCount(boothId: string): Promise<number> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VOTERS, 'readonly');
      const store = tx.objectStore(STORES.VOTERS);
      const index = store.index('pollingBoothId');
      const request = index.count(boothId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== VERIFICATIONS ====================

  async saveVerification(verification: OfflineVerification): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readwrite');
      const store = tx.objectStore(STORES.VERIFICATIONS);
      store.put(verification);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async saveVerifications(verifications: OfflineVerification[]): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readwrite');
      const store = tx.objectStore(STORES.VERIFICATIONS);

      for (const verification of verifications) {
        store.put(verification);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getVerifications(boothId: string): Promise<OfflineVerification[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readonly');
      const store = tx.objectStore(STORES.VERIFICATIONS);
      const index = store.index('pollingBoothId');
      const request = index.getAll(boothId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedVerifications(boothId: string): Promise<OfflineVerification[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readonly');
      const store = tx.objectStore(STORES.VERIFICATIONS);
      const index = store.index('booth_synced');
      // Get all records for this boothId where synced = false (0)
      const keyRange = IDBKeyRange.only([boothId, 0]);
      const request = index.getAll(keyRange);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedVerificationsAll(): Promise<OfflineVerification[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readonly');
      const store = tx.objectStore(STORES.VERIFICATIONS);
      const index = store.index('synced');
      const request = index.getAll(0); // 0 = unsynced (number for IndexedDB key compatibility)

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async markVerificationsSynced(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const db = await this.getDB();

    // Use cursor-based approach to avoid the async-get-inside-transaction anti-pattern
    // The old approach (store.get + onsuccess + store.put) could silently fail because
    // IndexedDB transactions auto-commit when no pending requests exist, and the
    // onsuccess callbacks from get() may not fire before the transaction completes.
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readwrite');
      const store = tx.objectStore(STORES.VERIFICATIONS);
      const idSet = new Set(ids);

      // Use cursor to iterate and update in-place - this is safe because
      // the cursor request keeps the transaction alive
      const request = store.openCursor();

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const record = cursor.value;
          if (idSet.has(record.id)) {
            record.synced = 1;
            record.syncedAt = new Date().toISOString();
            cursor.update(record);
            idSet.delete(record.id);
          }
          // Continue even if we've found all IDs - the transaction handles this efficiently
          if (idSet.size > 0) {
            cursor.continue();
          }
          // If all IDs found, we can stop early but transaction will complete naturally
        }
      };

      tx.oncomplete = () => {
        if (idSet.size > 0) {
          console.warn(`[OfflineDB] ${idSet.size} verification IDs not found during markSynced:`, Array.from(idSet));
        }
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  async getVerificationCount(boothId: string): Promise<number> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.VERIFICATIONS, 'readonly');
      const store = tx.objectStore(STORES.VERIFICATIONS);
      const index = store.index('pollingBoothId');
      const request = index.count(boothId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedCount(boothId: string): Promise<number> {
    const unsynced = await this.getUnsyncedVerifications(boothId);
    return unsynced.length;
  }

  // ==================== BOOTH INFO ====================

  async saveBoothInfo(info: OfflineBoothInfo): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.BOOTH_INFO, 'readwrite');
      const store = tx.objectStore(STORES.BOOTH_INFO);
      store.put(info);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getBoothInfo(boothId: string): Promise<OfflineBoothInfo | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.BOOTH_INFO, 'readonly');
      const store = tx.objectStore(STORES.BOOTH_INFO);
      const request = store.get(boothId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // ==================== SYNC QUEUE ====================

  async addToSyncQueue(item: SyncQueueItem): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      store.put(item);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.SYNC_QUEUE, 'readonly');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removeFromSyncQueue(ids: string[]): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);

      for (const id of ids) {
        store.delete(id);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // ==================== CREDENTIALS ====================

  async saveCredential(cred: OfflineCredential): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.CREDENTIALS, 'readwrite');
      const store = tx.objectStore(STORES.CREDENTIALS);
      store.put(cred);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getCredential(username: string): Promise<OfflineCredential | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.CREDENTIALS, 'readonly');
      const store = tx.objectStore(STORES.CREDENTIALS);
      const request = store.get(username);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async removeCredential(username: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.CREDENTIALS, 'readwrite');
      const store = tx.objectStore(STORES.CREDENTIALS);
      store.delete(username);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // ==================== UTILITY ====================

  async clearAll(): Promise<void> {
    const db = await this.getDB();
    const storeNames = [STORES.VOTERS, STORES.VERIFICATIONS, STORES.BOOTH_INFO, STORES.SYNC_QUEUE, STORES.CREDENTIALS];

    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeNames, 'readwrite');

      for (const storeName of storeNames) {
        tx.objectStore(storeName).clear();
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async clearBoothData(boothId: string): Promise<void> {
    const db = await this.getDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(
        [STORES.VOTERS, STORES.VERIFICATIONS, STORES.BOOTH_INFO],
        'readwrite'
      );

      // Clear voters for booth
      const voterStore = tx.objectStore(STORES.VOTERS);
      const voterIndex = voterStore.index('pollingBoothId');
      const voterRequest = voterIndex.openCursor(boothId);
      voterRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      // Clear verifications for booth
      const verifyStore = tx.objectStore(STORES.VERIFICATIONS);
      const verifyIndex = verifyStore.index('pollingBoothId');
      const verifyRequest = verifyIndex.openCursor(boothId);
      verifyRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      // Clear booth info
      const boothStore = tx.objectStore(STORES.BOOTH_INFO);
      boothStore.delete(boothId);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

// Singleton instance
let offlineDBInstance: OfflineDB | null = null;

export function getOfflineDB(): OfflineDB {
  if (!offlineDBInstance) {
    offlineDBInstance = new OfflineDB();
  }
  return offlineDBInstance;
}

export const offlineDB = getOfflineDB();
